import React, { useState } from "react";
import { Link } from "wouter";
import { SEOHead } from "@/components/SEOHead";
import { Header, Footer, Breadcrumbs } from "@/components/Navigation";
import { PRACTICE_INFO } from "@/site-data";
import {
  Calendar,
  Phone,
  Clock,
  MapPin,
  CheckCircle,
  AlertCircle,
  ShieldCheck,
  Send
} from "lucide-react";

export default function Appointments() {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    preferredDate: "",
    preferredTime: "Morning",
    reason: "",
    message: ""
  });
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.phone.trim() || !formData.email.trim()) {
      setStatus("error");
      setErrorMessage("Please complete all required fields (Name, Phone, and Email).");
      return;
    }

    setStatus("submitting");
    setTimeout(() => {
      setStatus("success");
    }, 600);
  };

  const appointmentSchema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    name: "Request an Appointment - Wasatch Foot & Ankle Center",
    description: "Request a consultation with board-certified podiatrist Dr. Spence Harper at Wasatch Foot & Ankle Center in Heber City, UT.",
    url: "https://www.drspenceharper.com/appointments/",
    mainEntity: {
      "@type": "MedicalClinic",
      name: "Wasatch Foot & Ankle Center",
      telephone: PRACTICE_INFO.phone,
      address: {
        "@type": "PostalAddress",
        streetAddress: "190 N. Main Street",
        addressLocality: "Heber City",
        addressRegion: "UT",
        postalCode: "84032",
        addressCountry: "US"
      }
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SEOHead
        title="Request Appointment & Contact | Wasatch Foot & Ankle Center Heber City"
        description="Book your podiatry appointment with Dr. Spence Harper in Heber City, UT. Submit your visit request online or call (435) 657-0329. Welcoming new patients."
        canonicalPath="/appointments/"
        schema={appointmentSchema}
      />
      <Header />
      <Breadcrumbs items={[{ label: "Appointments & Contact" }]} />

      <main className="flex-1">
        {/* HERO TITLE */}
        <section className="bg-[#0f2b48] text-white py-14">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-3">
            <span className="text-cyan-400 font-bold text-xs uppercase tracking-widest">
              Welcoming New Patients &bull; Easy Online Scheduling
            </span>
            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">
              Request an Appointment
            </h1>
            <p className="text-slate-300 text-base max-w-2xl mx-auto font-body">
              Take the first step toward lasting foot and ankle relief. Fill out our simple form below and our office staff will contact you promptly to confirm your visit.
            </p>
          </div>
        </section>

        {/* FORM & INFO SECTION */}
        <section className="py-16 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
              {/* Form Container */}
              <div className="lg:col-span-7 bg-white rounded-3xl p-8 sm:p-10 border border-slate-200 shadow-md">
                <div className="mb-6 space-y-2">
                  <h2 className="font-heading text-2xl font-bold text-[#0f2b48]">
                    Patient Appointment Request
                  </h2>
                  <p className="text-xs sm:text-sm text-slate-500 font-body">
                    Please use this form for general appointment scheduling. Availability varies by request, and a member of our office staff will review your information and call to confirm your appointment. Do not include protected health information; call <a href={`tel:${PRACTICE_INFO.officePhone}`} className="font-semibold text-[#0284c7]">{PRACTICE_INFO.officePhone}</a> if you need immediate assistance.
                  </p>
                </div>

                {status === "success" ? (
                  <div className="p-8 rounded-2xl bg-emerald-50 border border-emerald-200 text-center space-y-4 animate-in fade-in">
                    <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto">
                      <CheckCircle className="w-8 h-8" />
                    </div>
                    <h3 className="font-heading text-2xl font-bold text-emerald-900">
                      Call the Office to Confirm
                    </h3>
                    <p className="text-sm text-emerald-800 max-w-md mx-auto">
                      Thank you, <strong>{formData.name}</strong>. A member of our office staff will call <strong>(435) 657-0329</strong> to confirm availability and your appointment day and time.
                    </p>
                    <div className="pt-2">
                      <button
                        onClick={() => {
                          setStatus("idle");
                          setFormData({
                            name: "",
                            phone: "",
                            email: "",
                            preferredDate: "",
                            preferredTime: "Morning",
                            reason: "",
                            message: ""
                          });
                        }}
                        className="text-xs font-bold text-[#0284c7] hover:underline"
                      >
                        Return to the appointment form
                      </button>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                    {status === "error" && (
                      <div className="p-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-sm flex items-center gap-3">
                        <AlertCircle className="w-5 h-5 shrink-0" />
                        <span>{errorMessage}</span>
                      </div>
                    )}

                    <div>
                      <label htmlFor="name" className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1">
                        Full Name <span className="text-rose-500">*</span>
                      </label>
                      <input
                        id="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="John Doe"
                        className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#0284c7] focus:border-transparent text-sm"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="phone" className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1">
                          Phone Number <span className="text-rose-500">*</span>
                        </label>
                        <input
                          id="phone"
                          type="tel"
                          required
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          placeholder="(435) 555-0199"
                          className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#0284c7] focus:border-transparent text-sm"
                        />
                      </div>

                      <div>
                        <label htmlFor="email" className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1">
                          Email Address <span className="text-rose-500">*</span>
                        </label>
                        <input
                          id="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="john@example.com"
                          className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#0284c7] focus:border-transparent text-sm"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="preferredDate" className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1">
                          Preferred Date
                        </label>
                        <input
                          id="preferredDate"
                          type="date"
                          value={formData.preferredDate}
                          onChange={(e) => setFormData({ ...formData, preferredDate: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#0284c7] focus:border-transparent text-sm"
                        />
                      </div>

                      <div>
                        <label htmlFor="preferredTime" className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1">
                          Preferred Time of Day
                        </label>
                        <select
                          id="preferredTime"
                          value={formData.preferredTime}
                          onChange={(e) => setFormData({ ...formData, preferredTime: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#0284c7] focus:border-transparent text-sm bg-white"
                        >
                          <option value="Morning">Morning (8:00 AM – 12:00 PM)</option>
                          <option value="Afternoon">Afternoon (1:00 PM – 5:00 PM)</option>
                          <option value="First Available">First Available Opening</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="reason" className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1">
                        Reason for Visit / Condition
                      </label>
                      <select
                        id="reason"
                        value={formData.reason}
                        onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#0284c7] focus:border-transparent text-sm bg-white"
                      >
                        <option value="">-- Please select condition --</option>
                        <option value="Swift Microwave Wart Therapy">Swift Microwave Therapy (Plantar Warts)</option>
                        <option value="Foot or Ankle Surgery Consultation">Foot / Ankle Surgical Consultation</option>
                        <option value="Bunion Pain & Correction">Bunion Pain &amp; Correction</option>
                        <option value="Heel Pain / Plantar Fasciitis">Heel Pain / Plantar Fasciitis</option>
                        <option value="Ingrown Toenail">Ingrown Toenail Relief</option>
                        <option value="Morton's Neuroma / Ball of Foot Pain">Morton's Neuroma / Ball of Foot Pain</option>
                        <option value="Sports Injury or Fracture">Sports Injury / Fracture</option>
                        <option value="General Foot Checkup">General Foot &amp; Ankle Consultation</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1">
                        Additional Notes or Symptoms (Optional)
                      </label>
                      <textarea
                        id="message"
                        rows={3}
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        placeholder="Tell us any specific concerns, pain duration, or previous treatments tried..."
                        className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#0284c7] focus:border-transparent text-sm"
                      ></textarea>
                    </div>

                    <div className="pt-2">
                      <button
                        type="submit"
                        disabled={status === "submitting"}
                        className="w-full inline-flex items-center justify-center gap-2 bg-[#0284c7] hover:bg-[#0369a1] text-white font-bold py-4 px-6 rounded-xl shadow-lg transition-all transform active:scale-98"
                      >
                        {status === "submitting" ? (
                          <span>Submitting Request...</span>
                        ) : (
                          <>
                            <Send className="w-4 h-4" /> Submit Appointment Request
                          </>
                        )}
                      </button>
                    </div>

                    <p className="text-center text-xs text-slate-400">
                      Do not include medical records, diagnoses, or other protected health information. Call the office for secure intake instructions.
                    </p>
                  </form>
                )}
              </div>

              {/* Clinic Contact Sidebar */}
              <div className="lg:col-span-5 space-y-6">
                <div className="bg-[#0f2b48] text-white rounded-3xl p-8 space-y-6 shadow-xl">
                  <div className="space-y-2">
                    <span className="text-cyan-300 text-xs font-bold uppercase tracking-wider">
                      Need Faster Assistance?
                    </span>
                    <h3 className="font-heading text-2xl font-bold text-white">
                      Call Us Directly
                    </h3>
                    <p className="text-sm text-slate-300 font-body leading-relaxed">
                      If you are experiencing acute pain, an infected ingrown toenail, or need immediate assistance, call our clinic directly.
                    </p>
                  </div>

                  <div className="space-y-3">
                    <a
                      href={`tel:${PRACTICE_INFO.phone}`}
                      className="w-full inline-flex items-center justify-center gap-2 bg-[#0284c7] hover:bg-[#0369a1] text-white font-bold py-3.5 px-4 rounded-xl shadow transition-all"
                    >
                      <Phone className="w-4 h-4" /> Call: (435) 657-0329
                    </a>
                  </div>

                  <div className="pt-4 border-t border-slate-700/80 space-y-3 text-xs text-slate-300">
                    <div className="flex items-start gap-3">
                      <MapPin className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
                      <div>
                        <strong>Wasatch Foot &amp; Ankle Center</strong><br />
                        190 N. Main Street<br />
                        Heber City, UT 84032
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Clock className="w-4 h-4 text-cyan-400 shrink-0 mt-0.5" />
                      <div>
                        <strong>Office Hours:</strong><br />
                        Mon, Tue, Thu: 8:00 AM – 5:00 PM<br />
                        Wed: Closed for Dedicated Surgery
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <ShieldCheck className="w-4 h-4 text-cyan-400 shrink-0" />
                      <span>Accepting Most Major Insurance Providers</span>
                    </div>
                  </div>
                </div>

                {/* Patient Preparation Notice */}
                <div className="bg-white border border-slate-200 rounded-3xl p-6 space-y-3 shadow-sm">
                  <h4 className="font-heading font-bold text-base text-[#0f2b48]">
                    What to Bring to Your Visit
                  </h4>
                  <ul className="text-xs text-slate-600 space-y-2 font-body">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                      <span>Valid government photo ID and health insurance card</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                      <span>List of current medications, prior surgeries, or allergies</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                      <span>Athletic shoes, work boots, or orthotics you wear regularly</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
